import React, { useCallback, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

/**
 * Drag-to-resize for the DB Manager grids, shared by the normal and the
 * transposed view.
 *
 * The drag moves a guide line and nothing else; the width is applied once on
 * release. Writing a width on every mousemove means a full table relayout per
 * pixel, and with a colgroup also driving the widths the inline style fights
 * it — two layout systems arguing sixty times a second, which is what made the
 * first version feel like it was catching.
 *
 * The line is moved by writing to the DOM node directly rather than through
 * state: one render when the drag starts, one when it ends, none in between.
 */

/** Narrow enough to hide a column without losing its resize handle. */
export const MIN_COLUMN_WIDTH = 48;

export function useDbColumnResize({ onCommit, onSeed }) {
  const guideRef = useRef(null);
  const [guide, setGuide] = useState(null); // { x, top, height } | null

  const beginResize = useCallback((e, key) => {
    e.preventDefault();
    e.stopPropagation();
    const th = e.currentTarget.closest('th');
    if (!th) return;
    const table = th.closest('table');
    const startX = e.clientX;
    const startWidth = th.offsetWidth;

    // Pin every column at its current width before the table switches to a
    // fixed layout, or the ones that were never dragged jump to new sizes the
    // moment the layout mode changes.
    if (onSeed && table) onSeed(table);

    // Span the table, clipped to what is on screen: the container's height
    // alone leaves the line hanging below the last row when the result is
    // shorter than the pane, and the table's alone runs it off-screen when it
    // is longer. The intersection is the part with rows behind it.
    const wrap = table?.closest('.dbm-grid-wrap');
    const tableRect = (table || th).getBoundingClientRect();
    const wrapRect = wrap ? wrap.getBoundingClientRect() : tableRect;
    const top = Math.max(tableRect.top, wrapRect.top);
    const bottom = Math.min(tableRect.bottom, wrapRect.bottom);
    setGuide({ x: e.clientX, top, height: Math.max(0, bottom - top) });
    document.body.classList.add('dbm-resizing');

    let lastX = e.clientX;
    const onMove = (ev) => {
      lastX = ev.clientX;
      // Clamped so the guide never suggests a width the release would refuse.
      const minX = startX - (startWidth - MIN_COLUMN_WIDTH);
      const node = guideRef.current;
      if (node) node.style.left = `${Math.max(minX, lastX)}px`;
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.classList.remove('dbm-resizing');
      setGuide(null);
      onCommit(key, Math.max(MIN_COLUMN_WIDTH, startWidth + (lastX - startX)));
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [onCommit, onSeed]);

  // Portalled and position:fixed so the grid's own scroll container cannot clip
  // it, and no scroll-offset maths is needed.
  const guideNode = guide
    ? ReactDOM.createPortal(
        <div
          ref={guideRef}
          className="dbm-resize-guide"
          style={{ left: `${guide.x}px`, top: `${guide.top}px`, height: `${guide.height}px` }}
        />,
        document.body
      )
    : null;

  return { beginResize, guideNode };
}

/**
 * The handle itself. Rendered inside a `position: relative` <th>; the grab area
 * is wider than the line it draws and overhangs the header's edge, so the
 * pointer does not fall out of it on the way down.
 */
export function ColumnResizeHandle({ onBegin, onReset }) {
  return (
    <span
      className="dbm-col-resize"
      role="presentation"
      title="Drag to resize · double-click to reset"
      onClick={(e) => e.stopPropagation()}
      onMouseDown={onBegin}
      onDoubleClick={(e) => {
        e.stopPropagation();
        onReset();
      }}
    />
  );
}
